import { GetDeal_deal } from '../../graphql/queries/__generated__/GetDeal';
import {
  DataLayerEvent,
  DataLayerFormName,
  DataLayerFormStage,
  DataLayerType,
  DxpDataLayerEvent,
  DxpDataLayerEventNames,
} from '../../../../../../common/utilities/customDatalayerInterface';
import dxpDigitalDataAssignment from '../../../../../../common/utilities/dxpDigitalDataAssignment';
import { formatDataLayerDeal } from '../dxpDataLayerHandler';
import dxpDataLayerToggleVerification from '../../../../../../common/utilities/dxpDataLayerToggleVerification';
import dataLayerToggleVerification from '../../../../../../common/utilities/dataLayerToggleVerification';
import IDimensions from '../../../../../../common/model/IDimensions';
import digitalDataAssignment from '../../../../../../common/utilities/digitalDataAssignment';
import buildSelectedSpecData from './buildSelectedSpecData';
import { customerStorage } from '../../../../../../common/service/customerStorage';
import hashUtilities from '../../../../../../common/utilities/hashUtilities';
import { FinanceResultType, JourneyType } from '../../../../__generated__/globalTypes';
import { FinanceFormType } from '../../graphql/__generated__/globalTypes';

interface ConfirmationScreenDataLayerHandlerArgs {
  deal: GetDeal_deal;
  subunitToUnit?: number;
  dimensions?: IDimensions;
  decimalScale?: number;
}

const ConfirmationScreenDataLayerHandler = async ({
  deal,
  dimensions,
  subunitToUnit,
  decimalScale,
}: ConfirmationScreenDataLayerHandlerArgs): Promise<void> => {
  try {
    const isDataLayer = dataLayerToggleVerification(dimensions);
    const isDxpDataLayer = dxpDataLayerToggleVerification();
    const isOnlineOrder = deal.journeyType === JourneyType.online_order;

    if (isDataLayer) {
      const { email, phone } = deal.customer;
      const { postcode } = customerStorage.getCustomerData(dimensions) || {};
      const dealer = deal?.dealer;
      const emailHashed = await hashUtilities.hashSHA256(email);
      const phoneHashed = await hashUtilities.hashSHA256(phone);

      if (isOnlineOrder) {
        digitalDataAssignment({
          event: DataLayerEvent.FORM_NAVIGATE,
          form: {
            name: DataLayerFormName.CONFIGURATOR_STOCK_RESERVATION,
            stage: DataLayerFormStage.FINALISE_TRANSACTION,
            details: {
              configurator: {
                financeOption: deal.finance.type as FinanceResultType,
                selectedSpec: buildSelectedSpecData({ deal, dimensions }),
              },
            },
          },
          user: {
            emailHashed: emailHashed || null,
            phoneHashed: phoneHashed || null,
            postCode: postcode || null,
            preferredDealership: dealer?.label || null,
          },
        } as DataLayerType);
      } else {
        digitalDataAssignment({
          event: DataLayerEvent.FORM_NAVIGATE,
          form: {
            name: DataLayerFormName.CONFIGURATOR,
            stage: DataLayerFormStage.CHECKOUT_CONFIRMATION,
            details: {
              configurator: {
                dealId: deal.uuid,
                selectedSpec: buildSelectedSpecData({ deal, dimensions }),
              },
            },
          },
          user: {
            postCode: postcode || null,
            emailHashed: emailHashed || null,
            preferredDealership: dealer?.label || null,
          },
        });
      }
    }

    if (isDxpDataLayer) {
      const {
        car: {
          prices: {
            carPrice: { originalPrice },
            totalPrice,
          },
        },
        finance,
        dealer,
      } = deal;
      const simulation = finance?.simulation;
      const isFinance = finance?.type === FinanceFormType.finance;
      const formatPrice = (price: number) => Math.round(Number((price / subunitToUnit).toFixed(decimalScale)));

      dxpDigitalDataAssignment({
        event: DxpDataLayerEvent.CONFIGURATOR,
        eventName: DxpDataLayerEventNames.CHECKOUT_LEAD,
        ...formatDataLayerDeal(deal),
        vehiclePrice: formatPrice(originalPrice?.[0]?.money?.amount),
        paymentMethod: finance?.type,
        dealerPreference: dealer?.label,
        paymentPeriod: isFinance ? simulation?.loanTermPeriod?.value : 0,
        totalPrice: formatPrice(totalPrice?.[0]?.money?.amount),
        monthlyPayment: isFinance ? formatPrice(simulation?.paymentValue?.[0]?.amount) : 0,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('DataLayer error', err);
  }
};

export default ConfirmationScreenDataLayerHandler;
